import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnDestroy, AfterViewInit, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { PDFDocumentProxy, getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import { LoggerService } from 'src/app/handlers/logger.service';
import Tesseract from 'tesseract.js';
import { ActivatedRoute } from '@angular/router';
import {GridData, GridExtractionService, GridHighlightLocation} from 'src/app/services/grid-extraction.service';
import { Subscription } from 'rxjs';


export interface PdfJsViewerConfig {
  showControls?: boolean;
  showNavigation?: boolean;
  showZoomControls?: boolean;
  showActionControls?: boolean;
  showDownloadPdf?: boolean;
  showOpenInNewWindow?: boolean;
  showMinimizePdf?: boolean;
  disableGridMode?: boolean;
  initialScale?: number;
  maxScale?: number;
  minScale?: number;
  zoomIncrement?: number;
  downloadFileName?: string;
  continuousView?: boolean; // Enable continuous scrolling
  pageGap?: number; // Gap between pages in continuous view
  parentWindow?: string; // the parent window name
}

export interface ExtractedText {
  text: string;
  method: 'backendTesseract' | 'inBrowserTesseract';
  confidence?: number;
}

interface WordOverlayBox {
  left: number;
  top: number;
  width: number;
  height: number;
  text: string;
  confidence?: number;
  fontSize?: number;
}

@Component({
  selector: 'app-pdfjs-viewer',
  standalone: false,
  templateUrl: './pdfjs-viewer.component.html',
  styleUrls: ['./pdfjs-viewer.component.scss']
})
export class PdfjsViewerComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @ViewChild('canvasContainer', { static: false }) canvasContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('selectionOverlay', { static: false }) selectionOverlay!: ElementRef<HTMLDivElement>;
  // Grid Mode state
  @ViewChild('fabricCanvas', { static: false }) fabricCanvasElement!: ElementRef<HTMLCanvasElement>;
  @Output() onGridExtracted = new EventEmitter<GridData>();

  @Input() pdfUrl = '';
  @Input() config: Partial<PdfJsViewerConfig> = {};
  @Input() autoLoad = true;

  @Output() onPdfLoaded = new EventEmitter<PDFDocumentProxy>();
  @Output() onPdfError = new EventEmitter<string>();
  @Output() onPageChange = new EventEmitter<number>();
  @Output() onTextExtracted = new EventEmitter<ExtractedText>();
  @Output() onPdfOpenNewWindow = new EventEmitter<string>();
  @Output() onPdfMinimized = new EventEmitter<void>();

  // Grid Mode
  isGridMode = false;
  isGridExtracting = false;
  gridExtractionPanelData: GridData | null = null;
  showGridExtractionPanel = false;
  gridModePanelInlineStyles: { [key: string]: string } = {
    position: 'absolute',
    top: '60px',
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#ffffff',
    borderRadius: '6px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    padding: '8px 12px',
    zIndex: '15',
    minWidth: '400px'
  };

  // PDF state
  private pdfDocument: PDFDocumentProxy | null = null;
  private gridRectCompleteSub?: Subscription;

  currentPage = 1;
  totalPages = 0;
  currentScale = 1.0;
  scaleToFit = true;
  currentRotation = 0; // Track rotation in degrees
  pdfLoading = true;
  pdfError = '';
  currentPdfHash = '';

  // OCR state
  isOcrProcessing = false;
  private isSelecting = false;
  private selectionStart = { x: 0, y: 0 };
  private selectionEnd = { x: 0, y: 0 };
  highlightedCellBounds: { left: number; top: number; width: number; height: number } | null = null;
  highlightedCellLocation: GridHighlightLocation | null = null;
  showWordOverlay = false;
  useInBrowserOcr = false;
  wordOverlayRects: WordOverlayBox[] = [];

  // Default configuration
  private defaultConfig: PdfJsViewerConfig = {
    showControls: true,
    showNavigation: true,
    showZoomControls: true,
    showActionControls: true,
    showDownloadPdf: true,
    showOpenInNewWindow: false,
    showMinimizePdf: true,
    disableGridMode: false,
    initialScale: 1.0,
    maxScale: 5.0,
    minScale: 0.25,
    zoomIncrement: 0.25,
    downloadFileName: 'document.pdf',
    continuousView: false,
    pageGap: 10 // 10px gap between pages
  };

  mergedConfig: PdfJsViewerConfig = { ...this.defaultConfig };
  private isConfigFromRoute = false;

  constructor(
    private logger: LoggerService,
    private route: ActivatedRoute,
    private gridExtractionService: GridExtractionService

  ) {}

  ngOnInit(): void {
    GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.mjs';

    // For pop out mode - configuration is passed as URL parameters
    this.route.queryParams.subscribe(params => {
      if (params['url']) {
        this.isConfigFromRoute = true;
        this.pdfUrl = params['url'];
        if (params['config']) {
          try {
            const configFromUrl = JSON.parse(decodeURIComponent(params['config']));
            this.mergedConfig = { ...this.defaultConfig, ...configFromUrl };
          } catch (e) {
            this.logger.error('Failed to parse config from URL:', e);
            this.mergedConfig = { ...this.defaultConfig };
          }
        }
        this.loadPdf();
      } else {
        this.isConfigFromRoute = false;
        this.mergedConfig = { ...this.defaultConfig, ...this.config };
      }
    });

    this.currentScale = this.mergedConfig.initialScale || 1.0;

    this.gridRectCompleteSub = this.gridExtractionService.gridRectCompleted.subscribe(() => {
      this.handleGridRectCompleted();
    });
  }

  ngAfterViewInit(): void {
    if (this.autoLoad && this.pdfUrl) {
      this.loadPdf();
    }
  }

  ngOnDestroy(): void {
    this.gridRectCompleteSub?.unsubscribe();
    this.cleanupAfterDocument();
  }

  // ===== HOSTLISTENER: PDF HOTKEY NAVIGATION =====
  @HostListener('document:keydown', ['$event'])
  handlePdfHotkeys(event: KeyboardEvent): void {
    // Only if PDF is loaded
    if (!this.pdfDocument) return;

    switch(event.key) {
      case 'PageDown': // next page
        if (this.canNextPage) {
          this.nextPage();
        }
        event.preventDefault();
        break;

      case 'PageUp': // previous page
        if (this.canPrevPage) {
          this.previousPage();
        }
        event.preventDefault();
        break;
    }
  }

  // ===== Document loading =====
  async loadPdf(url?: string, newConfig?: Partial<PdfJsViewerConfig>): Promise<void> {
    const pdfUrl = url || this.pdfUrl;
    if (!pdfUrl) {
      this.pdfError = 'No PDF URL provided';
      return;
    }
    // Update configuration if provided
    if (newConfig) {
      this.mergedConfig = { ...this.defaultConfig, ...newConfig };
    }

    this.cleanupAfterDocument();
    this.pdfLoading = true;
    this.pdfError = '';

    // IMPORTANT: Ensure rotation starts at 0
    this.currentRotation = 0;

    try {
      this.pdfDocument = await getDocument(pdfUrl).promise;
      this.totalPages = this.pdfDocument.numPages;
      this.currentPage = 1;

      this.currentPdfHash = this.generateSimpleHash(pdfUrl);
      this.logger.debug(`[PDF] Loaded with hash: ${this.currentPdfHash}`);

      await this.renderAllPages();

      this.onPdfLoaded.emit(this.pdfDocument);
      this.logger.debug(`PDF loaded successfully: ${this.totalPages} pages`);
    } catch (error) {
      this.pdfError = `Failed to load PDF: ${error.message || error}`;
      this.onPdfError.emit(this.pdfError);
      this.logger.error(this.pdfError);
    } finally {
      this.pdfLoading = false;
    }
  }

  private generateSimpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
  }

  hasError(): boolean {
    return !!this.pdfError;
  }

  // ===== ZOOM METHODS =====
  zoomIn(): void {
    this.scaleToFit = false;
    const newScale = this.currentScale + this.mergedConfig.zoomIncrement!;
    if (newScale <= this.mergedConfig.maxScale!) {
      this.currentScale = newScale;
      this.renderAllPages();
      this.logger.debug(`Zoomed in to ${(this.currentScale * 100).toFixed(0)}%`);
    }
  }

  zoomOut(): void {
    this.scaleToFit = false;
    const newScale = this.currentScale - this.mergedConfig.zoomIncrement!;
    if (newScale >= this.mergedConfig.minScale!) {
      this.currentScale = newScale;
      this.renderAllPages();
      this.logger.debug(`Zoomed out to ${(this.currentScale * 100).toFixed(0)}%`);
    }
  }

  resetZoom(): void {
    this.scaleToFit = false;
    this.currentScale = 1.0;
    this.renderAllPages();
    this.logger.debug('Zoom reset to 100%');
  }

  async zoomToFit(): Promise<void> {
    this.scaleToFit = true;
    await this.renderAllPages();
    this.logger.debug('Zoomed to fit container width');
  }

  get canZoomIn(): boolean {
    return this.currentScale < this.mergedConfig.maxScale!;
  }

  get canZoomOut(): boolean {
    return this.currentScale > this.mergedConfig.minScale!;
  }

  // ===== ROTATION METHODS =====

  rotateClockwise(): void {
    this.currentRotation = (this.currentRotation + 90) % 360;
    this.renderAllPages();
    this.logger.debug(`Rotated clockwise to ${this.currentRotation}°`);
  }

  rotateCounterClockwise(): void {
    this.currentRotation = (this.currentRotation - 90 + 360) % 360;
    this.renderAllPages();
    this.logger.debug(`Rotated counter-clockwise to ${this.currentRotation}°`);
  }

  // ===== NAVIGATION METHODS =====

  nextPage(): void {
    if (this.canNextPage) {
      this.goToPage(this.currentPage + 1);
    }
  }

  previousPage(): void {
    if (this.canPrevPage) {
      this.goToPage(this.currentPage - 1);
    }
  }

  async goToPage(pageNumber: number): Promise<void> {
    if (pageNumber >= 1 && pageNumber <= this.totalPages && pageNumber !== this.currentPage) {
      this.currentPage = pageNumber;
      this.showPage(this.currentPage);
    }
  }

  get canNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  get canPrevPage(): boolean {
    return this.currentPage > 1;
  }

  // ===== PANEL CONTROL METHODS =====

  minimizePdfViewer(): void {
    this.onPdfMinimized.emit();
    this.logger.debug('PDF viewer minimized');
  }

  // ===== FILE OPERATIONS =====

  downloadPdf(): void {
    if (this.pdfUrl) {
      const link = document.createElement('a');
      link.href = this.pdfUrl;
      link.download = this.mergedConfig.downloadFileName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      this.logger.debug('PDF download initiated');
    }
  }

  openInNewWindow(): void {
    if (!this.pdfUrl) {
      return;
    }
    this.onPdfOpenNewWindow.emit(this.pdfUrl);
  }

  // ===== PDF canvas word selection handlers =====
  onCanvasMouseDown(event: MouseEvent): void {
    // Skip if grid mode is active or OCR is processing
    if (this.isGridMode || this.isOcrProcessing) return;

    this.isSelecting = true;

    const canvas = this.currentCanvas;
    const rect = canvas.getBoundingClientRect();
    this.selectionStart = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    this.selectionEnd = { ...this.selectionStart };
    this.showSelection();
  }

  onCanvasMouseMove(event: MouseEvent): void {
    // Skip if grid mode is active
    if (this.isGridMode || !this.isSelecting || this.isOcrProcessing) return;

    const canvas = this.currentCanvas;
    const rect = canvas.getBoundingClientRect();
    this.selectionEnd = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
    this.updateSelection();
  }

  onCanvasMouseUp(event: MouseEvent): void {
    // Skip if grid mode is active
    if (this.isGridMode || !this.isSelecting || this.isOcrProcessing) return;

    this.isSelecting = false;

    const minSelectionSize = 10;
    const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
    const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);

    if (width > minSelectionSize && height > minSelectionSize) {
      this.extractTextFromSelection();
    } else {
      this.hideSelection();
    }
  }

  onCanvasMouseLeave(): void {
    // Skip if grid mode is active
    if (this.isGridMode) return;

    if (this.isSelecting) {
      this.isSelecting = false;
      this.hideSelection();
    }
  }

  private showSelection(): void {
    if (this.selectionOverlay) {
      this.selectionOverlay.nativeElement.style.display = 'block';
    }
  }

  private hideSelection(): void {
    if (this.selectionOverlay) {
      this.selectionOverlay.nativeElement.style.display = 'none';
    }
  }

  private updateSelection(): void {
    if (!this.selectionOverlay) return;

    const overlay = this.selectionOverlay.nativeElement;
    const canvasRect = this.currentCanvas.getBoundingClientRect();
    const containerRect = this.canvasContainer.nativeElement.getBoundingClientRect();

    // Offset selection relative to canvas position inside container
    const offsetX = canvasRect.left - containerRect.left;
    const offsetY = canvasRect.top - containerRect.top;

    const left = Math.min(this.selectionStart.x, this.selectionEnd.x);
    const top = Math.min(this.selectionStart.y, this.selectionEnd.y);
    const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
    const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);

    overlay.style.left = `${offsetX + left}px`;
    overlay.style.top = `${offsetY + top}px`;
    overlay.style.width = `${width}px`;
    overlay.style.height = `${height}px`;
  }

  // ===== PRIVATE RENDERING METHODS =====
  private showPage(pageNumber: number): void {
    if (!this.canvasContainer) return;

    const container = this.canvasContainer.nativeElement;
    const canvases = container.querySelectorAll<HTMLCanvasElement>('canvas[data-page-number]');

    canvases.forEach(canvas => {
      const canvasPage = parseInt(canvas.dataset.pageNumber || '0', 10);
      const shouldShow = canvasPage === pageNumber;

      canvas.style.display = shouldShow ? 'block' : 'none';
    });

    if (this.isGridMode) {
      this.syncGridOverlayToCurrentPage();
    }

    this.onPageChange.emit(this.currentPage);
    this.refreshWordOverlay();
  }

  private async renderAllPages(): Promise<void> {
    if (!this.pdfDocument || !this.canvasContainer) return;

    const container = this.canvasContainer.nativeElement;

    // Remove old canvases
    const oldCanvases = container.querySelectorAll('canvas[data-page-number]');
    oldCanvases.forEach(c => c.remove());

    for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
      const page = await this.pdfDocument.getPage(pageNum);

      if (this.scaleToFit) {
        const parentElement = container.parentElement as HTMLElement | null;
        const hostWidth = parentElement?.clientWidth || container.clientWidth;
        if (hostWidth > 0) {
          const containerWidth = hostWidth * 0.98;
          const unscaledViewport = page.getViewport({ scale: 1.0 });
          this.currentScale = containerWidth / unscaledViewport.width;
        }
      }

      const viewport = page.getViewport({
        scale: this.currentScale,
        rotation: this.currentRotation
      });

      // Dev note: Can be refactored to use an angular template
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      canvas.style.display = 'none'; // Hide all pages initially
      canvas.dataset.pageNumber = pageNum.toString();
      canvas.classList.add('pdf-canvas');

      canvas.addEventListener('mousedown', this.onCanvasMouseDown.bind(this));
      canvas.addEventListener('mousemove', this.onCanvasMouseMove.bind(this));
      canvas.addEventListener('mouseup', this.onCanvasMouseUp.bind(this));
      canvas.addEventListener('mouseleave', this.onCanvasMouseLeave.bind(this));

      const context = canvas.getContext('2d');
      if (!context) continue;

      const renderContext = {
        canvasContext: context,
        viewport
      };

      await page.render(renderContext).promise;
      container.appendChild(canvas);
    }

    this.showPage(this.currentPage);
  }

  // ===== OCR METHODS =====
  private async extractTextFromSelection(): Promise<void> {
    if (this.isOcrProcessing) return;
    this.isOcrProcessing = true;

    try {
      if (!this.currentCanvas) {
        throw new Error('No PDF canvas available');
      }

      const method: 'backendTesseract' | 'inBrowserTesseract' = this.useInBrowserOcr
        ? 'inBrowserTesseract'
        : 'backendTesseract';
      const extractedText = this.useInBrowserOcr
        ? await this.extractSelectionTextWithTesseract()
        : this.extractSelectionTextFromBackend();

      if (extractedText.trim()) {
        this.handleTextExtraction({
          text: extractedText,
          method: method
        });
      }
    } catch (error) {
      this.logger.error(`OCR extraction failed: ${error}`);
    } finally {
      this.isOcrProcessing = false;
      this.hideSelection();
    }
  }

  private handleTextExtraction(extractedData: ExtractedText): void {
    // Check if this is a child window and send message to parent
    if (window.opener && this.mergedConfig?.parentWindow) {
      window.opener.postMessage({
        type: 'textExtracted',
        data: extractedData
      }, '*');
    }

    this.onTextExtracted.emit(extractedData);
  }

  private extractSelectionTextFromBackend(): string {
    if (!this.currentCanvas) {
      return '';
    }

    const pdfCanvas = this.currentCanvas;
    const pageKey = this.getCurrentGridPageKey();
    const textItems = this.gridExtractionService.getTextItemsForPage(
      pdfCanvas,
      this.currentPage,
      pageKey,
      'Extract Text'
    );

    const canvasRect = pdfCanvas.getBoundingClientRect();
    const scaleX = pdfCanvas.width / canvasRect.width;
    const scaleY = pdfCanvas.height / canvasRect.height;

    const left = Math.min(this.selectionStart.x, this.selectionEnd.x) * scaleX;
    const top = Math.min(this.selectionStart.y, this.selectionEnd.y) * scaleY;
    const width = Math.abs(this.selectionEnd.x - this.selectionStart.x) * scaleX;
    const height = Math.abs(this.selectionEnd.y - this.selectionStart.y) * scaleY;
    const right = left + width;
    const bottom = top + height;

    const selectedItems = textItems
      .filter((item: any) => {
        const itemRight = item.x + item.width;
        const itemBottom = item.y + item.height;
        const overlapWidth = Math.max(0, Math.min(itemRight, right) - Math.max(item.x, left));
        const overlapHeight = Math.max(0, Math.min(itemBottom, bottom) - Math.max(item.y, top));
        const overlapArea = overlapWidth * overlapHeight;
        const itemArea = (item.width || 0) * (item.height || 0);

        if (itemArea === 0) {
          return false;
        }

        return (overlapArea / itemArea) >= 0.5;
      })
      .sort((a: any, b: any) => {
        const rowTolerance = Math.max(a.height || 0, b.height || 0, 5);
        const deltaY = a.y - b.y;
        if (Math.abs(deltaY) > rowTolerance) {
          return deltaY;
        }

        return a.x - b.x;
      });

    return selectedItems
      .map((item: any) => item.text)
      .filter((text: string) => !!text?.trim())
      .join(' ')
      .trim();
  }

  private async extractSelectionTextWithTesseract(): Promise<string> {
    const canvas = this.currentCanvas;
    if (!canvas) return '';

    const left = Math.min(this.selectionStart.x, this.selectionEnd.x);
    const top = Math.min(this.selectionStart.y, this.selectionEnd.y);
    const width = Math.abs(this.selectionEnd.x - this.selectionStart.x);
    const height = Math.abs(this.selectionEnd.y - this.selectionStart.y);

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const scaledBounds = {
      left: left * scaleX,
      top: top * scaleY,
      width: width * scaleX,
      height: height * scaleY
    };

    const result = await this.recognizeCanvasRegionWithTesseract(canvas, scaledBounds);
    if (!result) {
      return '';
    }

    return result.data?.text?.trim() || '';
  }

  // on Grid Extraction box complete, pass the meta data for text extraction
  private async handleGridRectCompleted(): Promise<void> {
    if (!this.isGridMode || !this.currentCanvas || !this.gridExtractionService.hasGrid()) {
      return;
    }

    try {
      await this.gridExtractionService.autoDetectGridLinesFromSelection(
        this.currentCanvas,
        this.currentPage,
        this.getCurrentGridPageKey()
      );
    } catch (error) {
      this.logger.error(`[Grid Mode] Auto-detect grid failed: ${error}`);
    } finally {
      this.gridExtractionService.tryApplySavedColumnTemplateToCurrentGrid();
    }
  }

  toggleWordOverlay(): void {
    this.showWordOverlay = !this.showWordOverlay;

    if (this.showWordOverlay) {
      this.refreshWordOverlay();
    } else {
      this.wordOverlayRects = [];
    }
  }

  trackWordOverlay(index: number): number {
    return index;
  }

  toggleInBrowserOcr(): void {
    this.useInBrowserOcr = !this.useInBrowserOcr;
  }

  private async recognizeCanvasRegionWithTesseract(
    canvas: HTMLCanvasElement,
    bounds: { left: number; top: number; width: number; height: number }
  ): Promise<Tesseract.RecognizeResult | null> {
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    const normalizedLeft = Math.max(0, Math.floor(bounds.left));
    const normalizedTop = Math.max(0, Math.floor(bounds.top));
    const maxWidth = canvas.width - normalizedLeft;
    const maxHeight = canvas.height - normalizedTop;
    const normalizedWidth = Math.max(1, Math.floor(Math.min(bounds.width, maxWidth)));
    const normalizedHeight = Math.max(1, Math.floor(Math.min(bounds.height, maxHeight)));

    if (normalizedWidth <= 0 || normalizedHeight <= 0) {
      return null;
    }

    const tempCanvas = document.createElement('canvas');
    const tempContext = tempCanvas.getContext('2d');
    if (!tempContext) {
      return null;
    }

    tempCanvas.width = normalizedWidth;
    tempCanvas.height = normalizedHeight;

    const imageData = context.getImageData(normalizedLeft, normalizedTop, normalizedWidth, normalizedHeight);
    tempContext.putImageData(imageData, 0, 0);

    try {
      return await Tesseract.recognize(tempCanvas, 'eng', { logger: () => {} });
    } catch (error) {
      throw new Error(`Tesseract OCR failed: ${error}`);
    }
  }

  private async buildInBrowserGridTextItems(): Promise<any[]> {
    const canvas = this.currentCanvas;
    if (!canvas) {
      throw new Error('No PDF canvas available');
    }

    const gridBounds = this.gridExtractionService.getGridBoundsForPdfCanvas(canvas);
    if (!gridBounds) {
      throw new Error('Grid bounds unavailable for OCR');
    }

    const recognition = await this.recognizeCanvasRegionWithTesseract(canvas, gridBounds);
    if (!recognition) {
      return [];
    }

    const gridLeft = gridBounds.left;
    const gridTop = gridBounds.top;

    const textItems = (recognition.data?.words || [])
      .map(word => {
        const cleanText = word.text?.trim() || '';
        if (!cleanText || !word.bbox) {
          return null;
        }

        const width = word.bbox.x1 - word.bbox.x0;
        const height = word.bbox.y1 - word.bbox.y0;
        if (width <= 0 || height <= 0) {
          return null;
        }

        return {
          text: cleanText,
          x: gridLeft + word.bbox.x0,
          y: gridTop + word.bbox.y0,
          width,
          height,
          confidence: word.confidence
        };
      })
      .filter((item): item is { text: string; x: number; y: number; width: number; height: number; confidence: number } => !!item);

    this.logger.debug(`[Grid Mode] In-browser OCR generated ${textItems.length} text items.`);
    return textItems;
  }

  private refreshWordOverlay(): void {
    if (!this.showWordOverlay) {
      this.wordOverlayRects = [];
      return;
    }

    if (!this.currentCanvas || !this.canvasContainer) {
      return;
    }

    try {
      const pageKey = this.getCurrentGridPageKey();
      const textItems = this.gridExtractionService.getTextItemsForPage(
        this.currentCanvas,
        this.currentPage,
        pageKey,
        'Word Overlay'
      );

      const canvasRect = this.currentCanvas.getBoundingClientRect();
      const containerRect = this.canvasContainer.nativeElement.getBoundingClientRect();
      const offsetX = canvasRect.left - containerRect.left;
      const offsetY = canvasRect.top - containerRect.top;
      const scaleX = canvasRect.width / this.currentCanvas.width;
      const scaleY = canvasRect.height / this.currentCanvas.height;

      this.wordOverlayRects = textItems.map((item: any) => {
        const scaledWidth = item.width * scaleX;
        const scaledHeight = item.height * scaleY;
        const fontSize = Math.max(8, scaledHeight * 0.75);

        return {
          left: offsetX + (item.x * scaleX),
          top: offsetY + (item.y * scaleY),
          width: scaledWidth,
          height: scaledHeight,
          text: item.text,
          confidence: item.confidence,
          fontSize
        };
      });
    } catch (error) {
      this.logger.error(`[Word Overlay] Failed to render overlay: ${error}`);
      this.wordOverlayRects = [];
    }
  }

  // ===== CLEANUP =====

  private cleanupAfterDocument(): void {
    if (this.pdfDocument) {
      this.pdfDocument.destroy();
      this.pdfDocument = null;
    }

    if (this.isGridMode) {
      this.isGridMode = false;
      this.disableGridMode();
    }

    this.currentPage = 1;
    this.totalPages = 0;
    this.currentRotation = 0;
    this.pdfError = '';
    this.isOcrProcessing = false;
    this.isSelecting = false;
    this.wordOverlayRects = [];
    this.resetHighlightState();
  }

  // ===== GRID MODE METHODS =====
  async toggleGridMode(): Promise<void> {
    if (this.mergedConfig.disableGridMode && !this.isGridMode) {
      return;
    }

    this.isGridMode = !this.isGridMode;
    this.isGridMode ? await this.enableGridMode() : this.disableGridMode();
  }

  private async enableGridMode(): Promise<void> {
    if(!this.scaleToFit) {
      await this.zoomToFit();
    }

    this.syncGridOverlayToCurrentPage(true);
  }

  private disableGridMode(): void {
    this.gridExtractionService.saveAndCleanUpCanvas();
    this.collapseFabricCanvas();
    this.resetHighlightState();
  }

  private syncGridOverlayToCurrentPage(initialize = false): void {
    if (!this.isGridMode || !this.fabricCanvasElement || !this.currentCanvas) {
      return;
    }

    const canvasRect = this.currentCanvas.getBoundingClientRect();

    const displayWidth = canvasRect.width;
    const displayHeight = canvasRect.height;
    const pageKey = this.getCurrentGridPageKey();

    if (initialize) {    // on enabling grid mode
      this.gridExtractionService.initializeFabricCanvas(
        this.fabricCanvasElement.nativeElement,
        displayWidth,
        displayHeight,
        pageKey
      );
    } else {             // on navigation, scaling, rotating
      this.gridExtractionService.synchronizePageStateWithCanvas(pageKey, displayWidth, displayHeight);
    }

    this.restoreHighlightForCurrentPage();
  }

  private collapseFabricCanvas(): void {
    if (!this.fabricCanvasElement) {
      return;
    }

    const fabricCanvas = this.fabricCanvasElement.nativeElement;
    fabricCanvas.width = 0;
    fabricCanvas.height = 0;
  }

  clearGrid(): void {
    this.gridExtractionService.clearGrid();
    // Clear cell highlights when clearing grid
    this.resetHighlightState();
  }

  hasGrid(): boolean {
    return this.gridExtractionService.hasGrid();
  }

  onGridRowIgnoreChange(event: { rowIndex: number; ignored: boolean }): void {
    this.logger.debug(`[Grid Panel] Row ${event.rowIndex} ignore status: ${event.ignored}`);
  }

  onGridImportData(gridData: GridData): void {
    this.logger.debug('[Grid Panel] Importing grid data');
    this.onGridExtracted.emit(gridData);
    this.showGridExtractionPanel = false;
  }

  async extractGridDataAndShowPanel(): Promise<void> {
    if (!this.gridExtractionService.hasGrid()) {
      this.logger.error('No grid defined. Please draw a grid first.');
      return;
    }

    if (!this.currentCanvas) {
      this.logger.error('No PDF canvas available');
      return;
    }

    this.isGridExtracting = true;

    try {
      const pageKey = this.getCurrentGridPageKey();
      let textItemsOverride: any[] | undefined;

      if (this.useInBrowserOcr) {
        this.isOcrProcessing = true;
        try {
          textItemsOverride = await this.buildInBrowserGridTextItems();
        } finally {
          this.isOcrProcessing = false;
        }
      }

      const gridData = await this.gridExtractionService.extractGridData(
        this.currentCanvas!,
        this.currentPage,
        pageKey,
        textItemsOverride
      );
      this.onGridExtracted.emit(gridData);  // Just emit - parent handles display
      this.logger.debug('Grid data extracted');
    } catch (error) {
      this.logger.error(`Grid extraction failed: ${error}`);
    } finally {
      this.isGridExtracting = false;
    }
  }

  async highlightCellOnPdf(target: GridHighlightLocation): Promise<void> {
    if (!this.isGridMode) {
      this.logger.warn('[Grid Highlight] Grid mode must be enabled to highlight cells.');
      return;
    }

    const targetPageNumber = this.extractPageNumberFromKey(target.pageKey);
    if (targetPageNumber === null) {
      this.logger.warn(`[Grid Highlight] Unable to determine page number from key ${target.pageKey}`);
      return;
    }

    if (targetPageNumber !== this.currentPage) {
      await this.goToPage(targetPageNumber);
    }

    if (!this.currentCanvas || !this.canvasContainer) {
      this.logger.warn('[Grid Highlight] PDF canvas unavailable.');
      return;
    }

    const cellBounds = this.gridExtractionService.getCellDisplayBounds(target.row, target.col);
    if (!cellBounds) {
      this.logger.warn(`[Grid Highlight] No bounds found for cell [${target.row},${target.col}].`);
      return;
    }

    const canvas = this.currentCanvas;
    const canvasRect = canvas.getBoundingClientRect();
    const containerRect = this.canvasContainer.nativeElement.getBoundingClientRect();
    const offsetX = canvasRect.left - containerRect.left;
    const offsetY = canvasRect.top - containerRect.top;

    this.highlightedCellLocation = target;
    this.gridExtractionService.setHighlightedCell(target);
    this.highlightedCellBounds = {
      left: offsetX + cellBounds.left,
      top: offsetY + cellBounds.top,
      width: cellBounds.width,
      height: cellBounds.height
    };
  }

  preventCanvasContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  // ===== shared private util methods =====

  private get currentCanvas(): HTMLCanvasElement | null {
    if (!this.canvasContainer) return null;
    return this.canvasContainer.nativeElement.querySelector(
      `canvas[data-page-number="${this.currentPage}"]`
    );
  }

  getCurrentGridPageKey(): string {
    return `pdf-${this.currentPdfHash}-page-${this.currentPage}`;
  }

  private resetHighlightState(): void {
    this.highlightedCellBounds = null;
    this.highlightedCellLocation = null;
    this.gridExtractionService.setHighlightedCell(null);
  }

  private restoreHighlightForCurrentPage(): void {
    const highlight = this.gridExtractionService.getActiveHighlight();
    const currentPageKey = this.getCurrentGridPageKey();
    if (!highlight || highlight.pageKey !== currentPageKey) {
      this.highlightedCellLocation = null;
      this.highlightedCellBounds = null;
      return;
    }

    this.highlightedCellLocation = highlight;
    void this.highlightCellOnPdf(highlight);
  }

  private extractPageNumberFromKey(pageKey: string): number | null {
    const match = /page-(\d+)/i.exec(pageKey);
    return match ? parseInt(match[1], 10) : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !this.isConfigFromRoute) {
      this.mergedConfig = { ...this.defaultConfig, ...this.config };
    }
  }

}
